import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { DataserviceService } from '../dataservice.service';
import { AppComponent } from '../app.component';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';

@Component({
  selector: 'app-company-admin-payroll',
  templateUrl: './company-admin-payroll.component.html',
  styleUrls: ['./company-admin-payroll.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanyAdminPayrollComponent implements OnInit {
  currentUser: any;
  companyPayroll: any = null;
  selectedCycleDetails: any = null;
  loading = false;
  cycleDetailsLoading = false;
  
  selectedCycleId: string = '';
  
  // Mobile card expansion state
  expandedCycleCards = new Set<string>();
  expandedRepCards = new Set<string>();
  
  // Filters
  dateRange: Date[] = [];
  selectedUserId: string = '';
  
  constructor(
    private dataService: DataserviceService,
    private app: AppComponent,
    private message: NzMessageService,
    private modal: NzModalService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.currentUser = JSON.parse(localStorage.getItem('user')!);
    
    if (!this.currentUser?.company_id) {
      this.message.error('No company associated with your account');
      return;
    }
    
    this.loadCompanyPayrollData();
  }

  loadCompanyPayrollData() {
    this.loading = true;
    
    // Use the same grouped payroll endpoint with company admin flag
    this.dataService.getGroupedPayrollHistory(true) // ← Pass true for company admin view
      .subscribe({
        next: (groupedData) => {
          console.log('Raw grouped payroll data:', groupedData);
          
          // Transform the grouped data to match company admin format
          this.companyPayroll = this.transformGroupedDataToCompanyFormat(groupedData);
          console.log(this)
          this.loading = false;
          this.cdr.markForCheck();
          console.log('Transformed company payroll data:', this.companyPayroll);
        },
        error: (error) => {
          console.error('Error loading company payroll:', error);
          this.message.error('Failed to load company payroll data');
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
  }

  transformGroupedDataToCompanyFormat(groupedData: any[]): any {
    // Transform to company admin format (company filtering now handled by backend)
    const payrollCycles = groupedData.map(group => ({
      id: group.id,
      payDate: group.payDate,
      startDate: group.cycles[0]?.startDate,
      endDate: group.cycles[0]?.endDate,
      status: group.status,
      totalCommissions: group.totalCommissions || 0,
      totalHoldbacks: group.totalHoldbacks || 0,
      totalAdjustments: group.totalAdjustments || 0,
      netPay: group.netPay || 0,
      repsPaid: group.repsPaid || 0,
      totalAmount: group.netPay || 0,
      repCount: group.payees.length,
      cycleCount: group.cycles.length,
      groupData: group
    }));

    // Calculate totals from paid cycles only
    const paidCycles = payrollCycles.filter(c => c.status === 'paid');
    const totalCommissions = paidCycles.reduce((sum, c) => sum + (c.totalCommissions || 0), 0);
    const totalHoldbacks = paidCycles.reduce((sum, c) => sum + (c.totalHoldbacks || 0), 0);
    const totalAdjustments = paidCycles.reduce((sum, c) => sum + (c.totalAdjustments || 0), 0);
    const totalPaid = paidCycles.reduce((sum, c) => sum + (c.netPay || 0), 0);

    return {
      companyInfo: {
        name: 'Company'
      },
      payrollCycles,
      totalPaid,
      totalCommissions,
      totalHoldbacks,
      totalAdjustments
    };
  }

  private transformGroupedDataToCycleDetails(groupData: any): any[] {
    console.log('Group data:', groupData);
    // Transform payees data to match the expected cycle details format
    return groupData.payees.map((payee: any) => ({
      userId: payee.payeeId,
      userName: payee.payeeName,
      salesCount: new Set((payee.customers || []).map((c: any) => c.customerId)).size,
      totalCommissions: payee.totalCommission || 0,
      totalHoldbacks: payee.totalHoldbacks || 0,
      totalAdjustments: payee.totalAdjustments || 0,
      netPay: payee.netPay || 0,
      commissions: (payee.customers || []).filter((c: any) => c.recordType === 'commission'),
      holdbacks: (payee.customers || []).filter((c: any) => c.recordType === 'holdback'),
      adjustments: (payee.customers || []).filter((c: any) => c.recordType === 'adjustment')
    }));
  }

  loadCycleDetails(cycleId: string) {
    if (this.selectedCycleId === cycleId) {
      // Toggle off if already selected
      this.selectedCycleId = '';
      this.selectedCycleDetails = null;
      return;
    }

    this.cycleDetailsLoading = true;
    this.selectedCycleId = cycleId;
    // Find the cycle in our grouped data
    const cycle = this.companyPayroll.payrollCycles.find((c: any) => c.id === cycleId);
  
    if (cycle && cycle.groupData) {
      // Transform grouped data to cycle details format
      this.selectedCycleDetails = this.transformGroupedDataToCycleDetails(cycle.groupData);
      this.cycleDetailsLoading = false;
      this.cdr.markForCheck();
      console.log('Loaded cycle details from grouped data:', this.selectedCycleDetails);
    } else {
      console.error('Cycle not found in grouped data');
      this.message.error('Failed to load cycle details');
      this.cycleDetailsLoading = false;
      this.selectedCycleId = '';
      this.cdr.markForCheck();
    }
  }

  isCycleExpanded(cycleId: string): boolean {
    return this.selectedCycleId === cycleId;
  }

  trackByCycleId(index: number, cycle: any): string {
    return cycle.id;
  }

  trackByUserId(index: number, user: any): string {
    return user.userId;
  }

  exportToCSV() {
    if (!this.selectedCycleId) {
      this.message.warning('Please select a payroll cycle first');
      return;
    }

    console.log('Exporting detailed cycle data for cycle:', this.selectedCycleId);
    

    // Find the selected cycle to get the actual cycle IDs
    const selectedCycle = this.companyPayroll.payrollCycles.find((c: any) => c.id === this.selectedCycleId);
    if (!selectedCycle || !selectedCycle.groupData || !selectedCycle.groupData.cycles) {
      this.message.error('Unable to find cycle details for export');
      return;
    }

    // Extract actual cycle IDs from the group data
    const actualCycleIds = selectedCycle.groupData.cycles.map((cycle: any) => cycle.id);
    console.log('Using actual cycle IDs for export:', actualCycleIds);

    // Get detailed export data from backend
    this.dataService.getCycleCustomerDetails(actualCycleIds, this.currentUser.id)
      .subscribe({
        next: (detailedData) => {
          console.log('Received detailed export data:', detailedData);
          
          const csvData = [];
          
          csvData.push([
            'Payee', 'Original Rep Name', 'Account Number', 'Order Id', 'Customer Name', 
            'State', 'Product', 'Plan', 'Sale Date', 'Activation Date', 'Commission Amount', 'Holdback',
            'Adjustment', 'Net Pay', 'Note', 'Pay Cycle Date'
          ]);

          // {
          //   "activationDate": "2025-10-03T06:00:00.000Z",
          //   "customerId": "4aa2208b-87d6-4b10-899b-d61e0d2168bd",
          //   "customerName": "SKYLER SCHILLER",
          //   "customerNameBackup": "SKYLER SCHILLER",
          //   "leadType": "OLD",
          //   "netPay": 510,
          //   "originalSalesRep": "Devin Hertz",
          //   "payType": "commission",
          //   "payTypeAnalysis": [
          //     { ... },
          //     { ... }
          //   ],
          //   "plan": "5 GB",
          //   "product": "ZIPLY",
          //   "ruleDescription": "ZIPLY - 5 GB (Imported Commission)",
          //   "salesCount": 1,
          //   "totalAdjustments": 0,
          //   "totalCommissions": 600,
          //   "totalHoldbacks": -90,
          //   "totalPaidAllTime": 600,
          //   "workOrderId": null
          // }
          

          detailedData.forEach((record: any) => {
            csvData.push([
              record.payee || this.currentUser.first_name + ' ' + this.currentUser.last_name,
              record.originalSalesRep || 'N/A',
              record.account_number || 'N/A',
              record.order_id || 'N/A',
              record.customerName || 'N/A',
              record.state || 'N/A',
              record.product || 'N/A',
              record.plan || 'N/A',
              record.sales_date ? new Date(record.sales_date).toLocaleDateString() : 'N/A',
              record.activationDate ? new Date(record.activationDate).toLocaleDateString() : 'N/A',
              record.totalCommissions || 0,
              record.totalHoldbacks || 0,
              record.totalAdjustments || 0,
              record.netPay || 0,
              record.note || 'N/A',
              record.pay_date ? new Date(record.pay_date).toLocaleDateString() : 'N/A'
            ]);
          });

          const selectedCycle = this.companyPayroll.payrollCycles.find((c: any) => c.id === this.selectedCycleId);
          const cycleDate = selectedCycle ? new Date(selectedCycle.payDate).toISOString().split('T')[0] : 'unknown';
          
          this.downloadCSV(csvData, `${this.companyPayroll.companyInfo.name}-detailed-cycle-${cycleDate}.csv`);
        },
        error: (error) => {
          console.error('Failed to get detailed export data:', error);
          this.message.error('Failed to export detailed cycle data');
        }
      });
  }

  private downloadCSV(data: any[][], filename: string) {
    const csvContent = data.map(row => 
      row.map(field => 
        typeof field === 'string' && field.includes(',') 
          ? `"${field}"` 
          : field
      ).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  formatCurrency(amount: number): string {
    if (amount === null || amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  getStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'paid': return 'green';
      case 'pending': return 'orange';
      case 'released': return 'blue';
      case 'approved': return 'green';
      default: return 'default';
    }
  }

  getPayTypeDisplay(payType: string): string {
    switch (payType?.toLowerCase()) {
      case 'commission':
        return 'Commission';
      case 'override':
        return 'Override';
      case 'adjustment':
        return 'Adjustment';
      default:
        return 'Commission';
    }
  }

  // Mobile card expansion methods
  toggleCycleCardExpansion(cycleId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.expandedCycleCards.has(cycleId)) {
      this.expandedCycleCards.delete(cycleId);
    } else {
      this.expandedCycleCards.add(cycleId);
    }
    this.cdr.markForCheck();
  }

  isCycleCardExpanded(cycleId: string): boolean {
    return this.expandedCycleCards.has(cycleId);
  }

  toggleRepCardExpansion(userId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.expandedRepCards.has(userId)) {
      this.expandedRepCards.delete(userId);
    } else {
      this.expandedRepCards.add(userId);
    }
    this.cdr.markForCheck();
  }

  isRepCardExpanded(userId: string): boolean {
    return this.expandedRepCards.has(userId);
  }

  // Filter methods
  applyFilters(): void {
    this.loadCompanyPayrollData();
  }

  clearFilters(): void {
    this.dateRange = [];
    this.selectedUserId = '';
    this.loadCompanyPayrollData();
  }
}
